/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Fragment, ReactNode } from "react";
import { If, Then, Else } from "react-if";
import { FlexContainer, Text, theme, Box, BrickLoader } from "@nordcloud/gnui";
import { LoaderWrap } from "~/components/Loader/LoaderWrap";
import { ChartLegend } from "~/views/dashboard/components/ChartLegend/ChartLegend";
import { StatusChart } from "~/views/dashboard/components/StatusChart/StatusChart";
import type { StatusChartItem } from "~/views/dashboard/components/types";

export type DetailsItem = {
  label: string;
  value: ReactNode;
};

type Props = {
  detailsItems: DetailsItem[];
  updateStatusChartItems: StatusChartItem[];
  loadingStatus?: boolean;
  hasUpdates: boolean;
};

const detailCardStyles = {
  flex: 1,
  flexDirection: "column" as const,
  display: "flex",
  gap: theme.spacing.spacing04,
  minHeight: "196px",
  height: "100%",
  alignSelf: "stretch",
};

const summaryCardStyles = {
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  gap: theme.spacing.spacing04,
  minHeight: "196px",
  height: "100%",
  alignSelf: "stretch",
};

export function UpdatesOverviewHeader({
  detailsItems,
  updateStatusChartItems,
  loadingStatus,
  hasUpdates,
}: Props) {
  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing.spacing04,
        width: "100%",
        alignItems: "stretch",
      }}
    >
      <Box boxStyle="lightGrey" css={detailCardStyles}>
        <Text weight="medium" size="md" css={{ margin: "unset" }}>
          Details
        </Text>
        <div css={{ wordBreak: "break-word" }}>
          <div
            css={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: theme.spacing.spacing04,
            }}
          >
            {detailsItems.map(({ label, value }) => (
              <Fragment key={label}>
                <Text as="span" css={{ margin: "unset" }}>
                  {label}:
                </Text>
                {value}
              </Fragment>
            ))}
          </div>
        </div>
      </Box>
      <Box boxStyle="lightGrey" css={summaryCardStyles}>
        <LoaderWrap
          loading={!!loadingStatus}
          Component={() => <BrickLoader height="170px" />}
        >
          <>
            <Text weight="medium" size="md" css={{ margin: "unset" }}>
              Summary
            </Text>

            <If condition={!hasUpdates}>
              <Then>
                <FlexContainer
                  gap={theme.spacing.spacing04}
                  alignItems="center"
                >
                  <div css={{ width: "125px", height: "125px" }}>
                    <StatusChart
                      items={updateStatusChartItems}
                      chartLabel={`Updates`}
                    />
                  </div>
                  <Text size="md" css={{ textAlign: "center", flex: 1 }}>
                    There are no updates.
                  </Text>
                </FlexContainer>
              </Then>
              <Else>
                <FlexContainer
                  gap={theme.spacing.spacing04}
                  alignItems={"center"}
                >
                  <div css={{ width: "125px", height: "125px" }}>
                    <StatusChart
                      items={updateStatusChartItems}
                      chartLabel={`Updates`}
                    />
                  </div>
                  <ChartLegend
                    items={updateStatusChartItems}
                    itemsPerColumn={3}
                  />
                </FlexContainer>
              </Else>
            </If>
          </>
        </LoaderWrap>
      </Box>
    </div>
  );
}
